import React, { FC, useState, useCallback } from "react";
import { TitleHeader, LoadingSpinner, PageAlert, Input, Header, validatePhoneNumber } from "@xapp/components";
import { UserNotificationPreferences, AlertSubscription, SummarySubscription } from "generated/graphql";
import { AlertSubscriptionList } from "./AlertSubscriptionList";
import { SummarySubscriptionList } from "./SummarySubscriptionList";
import { SubscribableApp } from "../types";

export interface NotificationPreferencesViewProps {
    preferences?: UserNotificationPreferences | null;
    /**
     * Set only when the query actually FAILED. Absent preferences is not an error — it is the state
     * every user starts in, and this form is the only way out of it.
     */
    error?: { message?: string } | null;
    subscribableApps: SubscribableApp[];
    loading: boolean;
    onPhoneUpdate: (phone: string) => void;
    onCreateAlertSubscription: (subscription: Partial<AlertSubscription>) => void;
    onUpdateAlertSubscription: (subscriptionId: string, subscription: Partial<AlertSubscription>) => void;
    onDeleteAlertSubscription: (subscriptionId: string) => void;
    onCreateSummarySubscription: (subscription: Partial<SummarySubscription>) => void;
    onUpdateSummarySubscription: (subscriptionId: string, subscription: Partial<SummarySubscription>) => void;
    onDeleteSummarySubscription: (subscriptionId: string) => void;
}

export const NotificationPreferencesView: FC<NotificationPreferencesViewProps> = (props) => {
    const {
        preferences,
        error,
        subscribableApps,
        loading,
        onPhoneUpdate,
        onCreateAlertSubscription,
        onUpdateAlertSubscription,
        onDeleteAlertSubscription,
        onCreateSummarySubscription,
        onUpdateSummarySubscription,
        onDeleteSummarySubscription,
    } = props;

    const [phoneNumber, setPhoneNumber] = useState(preferences?.phone || "");
    const [phoneError, setPhoneError] = useState("");

    const handlePhoneChange = useCallback((value: string) => {
        setPhoneNumber(value);
        if (phoneError) setPhoneError("");
    }, [phoneError]);

    const handlePhoneBlur = useCallback(() => {
        if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
            setPhoneError("Invalid phone number format");
        } else {
            setPhoneError("");
            onPhoneUpdate(phoneNumber);
        }
    }, [phoneNumber, onPhoneUpdate]);

    if (loading) {
        return <LoadingSpinner loadingText="Loading notification preferences..." />;
    }

    if (error) {
        return (
            <div>
                <TitleHeader value="Notification Preferences" />
                <PageAlert background="red">
                    <p>Couldn't load your notification preferences. Reload the page to try again.</p>
                </PageAlert>
            </div>
        );
    }

    // Deliberately NO early return for absent preferences: a user who has never subscribed has no
    // record, and rendering an error there meant they could never create one. The form below is the
    // empty state.

    if (subscribableApps.length === 0) {
        return (
            <div>
                <TitleHeader value="Notification Preferences" />
                <PageAlert background="blue">
                    <p>You don't have access to any apps yet. Once you have access to apps, you can subscribe to notifications here.</p>
                </PageAlert>
            </div>
        );
    }

    return (
        <div>
            <TitleHeader value="Notification Preferences" />

            <div style={{ marginBottom: "32px" }}>
                <Header level={3}>Contact Information</Header>
                <Input
                    label="Email"
                    value={preferences?.email || ""}
                    disabled
                    variation="outlined"
                    helperText="Your email address from your profile"
                />
                <Input
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={(val) => handlePhoneChange(val as string)}
                    onBlur={handlePhoneBlur}
                    variation="outlined"
                    helperText={phoneError || "For future SMS notifications (optional)"}
                    placeholder="+1234567890"
                    error={!!phoneError}
                />
            </div>

            <div style={{ marginBottom: "32px" }}>
                <Header level={3}>Real-Time Alerts</Header>
                <PageAlert background="blue">
                    <p>Get notified immediately when specific events occur in your apps.</p>
                </PageAlert>
                <AlertSubscriptionList
                    subscriptions={preferences?.alertSubscriptions || []}
                    subscribableApps={subscribableApps}
                    onCreate={onCreateAlertSubscription}
                    onUpdate={onUpdateAlertSubscription}
                    onDelete={onDeleteAlertSubscription}
                />
            </div>

            <div style={{ marginBottom: "32px" }}>
                <Header level={3}>Summary Reports</Header>
                <PageAlert background="blue">
                    <p>Receive periodic summaries of activity across your apps.</p>
                </PageAlert>
                <SummarySubscriptionList
                    subscriptions={preferences?.summarySubscriptions || []}
                    subscribableApps={subscribableApps}
                    onCreate={onCreateSummarySubscription}
                    onUpdate={onUpdateSummarySubscription}
                    onDelete={onDeleteSummarySubscription}
                />
            </div>
        </div>
    );
};
